import { PrintBridge, type PrinterInfo } from '@/bridge/print.bridge';
import { API_BASE } from '@/lib/api-url';

const LOCAL_STORAGE_PRINTER_IP = 'printer-selected-ip';
const SECRET_KEY = import.meta.env.VITE_SECRET_KEY ?? '';

type WebPrinter = {
  ip: string;
  port?: number;
  name?: string;
  model?: string;
};

function webApiHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', 'x-secret-key': SECRET_KEY };
}

export class PrinterLocalService {
  private static instance: PrinterLocalService | null = null;
  public ip: string | undefined = undefined;
  public port: number = 9100;
  public printers: PrinterInfo[] = [];
  public discoveryPromise: Promise<PrinterInfo[] | null> | null = null;

  private constructor() {
    // Le nettoyage est rattaché à la promesse affectée : un retour synchrone de
    // discoverPrinters (pas de bridge Capacitor, ex. navigateur) exécuterait un finally
    // interne AVANT l'affectation, laissant discoveryPromise non-nul à jamais.
    this.discoveryPromise = this.discoverPrinters().finally(() => {
      this.discoveryPromise = null;
    });
  }

  public static getInstance(): PrinterLocalService {
    if (!PrinterLocalService.instance) {
      PrinterLocalService.instance = new PrinterLocalService();
    }
    return PrinterLocalService.instance;
  }

  public static reset(): void {
    PrinterLocalService.instance = null;
  }

  get printerAvailable(): boolean {
    return !!this.ip;
  }

  get isDiscovering(): boolean {
    return this.discoveryPromise !== null;
  }

  private selectPrinter(printers: PrinterInfo[]): PrinterInfo | null {
    if (printers.length === 0) return null;

    const savedIp = localStorage.getItem(LOCAL_STORAGE_PRINTER_IP);

    if (savedIp) {
      const savedPrinter = printers.find(p => p.ip === savedIp);
      if (savedPrinter) {
        return savedPrinter;
      }
    }

    const okiPrinter = printers.find(
      p => p.description?.toLowerCase().includes('oki') || p.model?.toLowerCase().includes('oki'),
    );
    if (okiPrinter) {
      return okiPrinter;
    }

    return printers[0];
  }

  async discoverPrinters(): Promise<PrinterInfo[] | null> {
    try {
      if (this.ip) {
        return this.printers.length > 0 ? this.printers : null;
      }

      const Printer = PrintBridge.getPrinter();
      const result = Printer
        ? await Printer.findPrinter({ port: this.port })
        : await this.findPrintersWeb();

      if (result && result.status === 'found' && result.printers.length > 0) {
        this.printers = result.printers;
        const selected = this.selectPrinter(result.printers);
        if (selected) {
          this.ip = selected.ip;
          localStorage.setItem(LOCAL_STORAGE_PRINTER_IP, selected.ip);
        }
        return result.printers;
      }
      return null;
    } catch (error) {
      console.error('Error discovering printers:', error);
      return null;
    }
  }

  setSelectedPrinter(ip: string): void {
    const printer = this.printers.find(p => p.ip === ip);
    if (printer) {
      this.ip = printer.ip;
      localStorage.setItem(LOCAL_STORAGE_PRINTER_IP, ip);
    }
  }

  async testConnection(): Promise<boolean> {
    try {
      if (!this.ip) {
        //console.error('Aucune imprimante trouvée pour le test de connexion');
        return false;
      }

      const Printer = PrintBridge.getPrinter();
      if (!Printer) {
        // Web: the browser sandbox cannot reach printers, the backend relays the job.
        const printers = await this.findPrintersWeb();
        return !!printers?.printers.some(printer => printer.ip === this.ip);
      }

      const result = await Printer.testConnection({
        host: this.ip,
        port: this.port,
      });

      //console.log('Test de connexion réussi:', result);
      return result.status === 'connected';
    } catch (error) {
      console.error('Test de connexion échoué:', error);
      return false;
    }
  }

  async printText(text: string): Promise<boolean> {
    // Imprimer via un mini-PDF : même pipeline que le CV, valide sur toutes les imprimantes
    return this.printPDF(textToPdfBlob(text));
  }

  async printPDF(pdfBlob: Blob): Promise<boolean> {
    try {
      if (!this.ip) {
        //console.error("Aucune imprimante trouvée pour l'impression PDF");
        return false;
      }

      // Convertir le blob PDF en base64
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const base64Data = this.arrayBufferToBase64(arrayBuffer);

      const Printer = PrintBridge.getPrinter();
      if (!Printer) {
        // Web: the browser sandbox cannot reach printers, the backend relays the job.
        return this.printPDFWeb(base64Data);
      }

      const result = await Printer.print({
        host: this.ip,
        port: this.port,
        base64Data,
      });

      //console.log('Impression PDF réussie:', result);
      return result.status === 'printed';
    } catch (error) {
      console.error("Erreur d'impression PDF:", error);
      return false;
    }
  }

  async printFromURL(url: string): Promise<boolean> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error('Téléchargement du PDF impossible:', response.status);
        return false;
      }
      const blob = await response.blob();
      return await this.printPDF(blob);
    } catch (error) {
      console.error('Erreur lors du téléchargement du fichier:', error);
      return false;
    }
  }

  async getPrinterStatus() {
    try {
      if (!this.ip) {
        //console.error('Aucune imprimante trouvée pour récupérer le statut');
        return null;
      }

      const result = await PrintBridge.getPrinterStatus({
        host: this.ip,
        port: this.port,
        timeout: 2000,
      });

      if (result.status === 'success' && result.printerStatus) {
        return {
          status: 'found',
          ip: this.ip,
          printerStatus: result.printerStatus,
        };
      } else {
        //console.error('Erreur lors de la récupération du statut:', result.error);
        return null;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des infos imprimante:', error);
      return null;
    }
  }

  // Fonction utilitaire pour convertir ArrayBuffer en base64
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private async findPrintersWeb(): Promise<{
    status: 'found' | 'not_found';
    printers: PrinterInfo[];
  } | null> {
    const response = await fetch(`${API_BASE}/print/printers`, { headers: webApiHeaders() });
    if (!response.ok) {
      console.error('Printers discovery failed:', response.status);
      return null;
    }
    const data = (await response.json()) as { printers: WebPrinter[] };
    const printers: PrinterInfo[] = data.printers.map(printer => ({
      ip: printer.ip,
      port: printer.port,
      description: printer.name,
      model: printer.model,
    }));
    return { status: printers.length > 0 ? 'found' : 'not_found', printers };
  }

  private async printPDFWeb(base64Data: string): Promise<boolean> {
    const response = await fetch(`${API_BASE}/print/pdf`, {
      method: 'POST',
      headers: webApiHeaders(),
      body: JSON.stringify({ host: this.ip, base64Data }),
    });
    return response.ok;
  }
}

// Génère un mini-PDF 1 page contenant le texte (le pipeline d'impression PDF est le seul chemin
// testé et validé sur toutes les imprimantes, y compris HP).
function textToPdfBlob(text: string): Blob {
  const lines = text
    .split('\n')
    .map(line => line.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)'));

  const contentParts = ['BT', '/F1 12 Tf', '50 780 Td', '14 TL'];
  lines.forEach((line, index) => {
    if (index > 0) contentParts.push('T*');
    contentParts.push(`(${line}) Tj`);
  });
  contentParts.push('ET');
  const content = contentParts.join('\n');

  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
  ];

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((body, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  const zero10 = '0'.repeat(10);
  const zero5 = '0'.repeat(5);
  pdf += `xref\n0 ${objects.length + 1}\n${zero10} 65535 f \n`;
  for (const offset of offsets) {
    pdf += `${String(offset).padStart(10, '0')} ${zero5} n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  const bytes = new Uint8Array(pdf.length);
  for (let i = 0; i < pdf.length; i++) {
    bytes[i] = pdf.charCodeAt(i) & 0xff;
  }
  return new Blob([bytes], { type: 'application/pdf' });
}
